import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { AppConfigService } from '../../app-config.service';
import { KreditorService } from '../../shared/kreditor.service';
import { ICaseRegistered as INewCaseRegistered, ICountry, INewCase, INewInvoice, IRecentlyCreatedCase, ServiceResult, IUploadFileResult } from '../../shared/models';
import { UtilsService } from '../../shared/utils.service';
import { CasesService } from 'src/app/services/cases.service';

@Injectable({
  providedIn: 'root'
})
export class NewCaseService {
  constructor(
    private kreditorWs: KreditorService,
    private config: AppConfigService,
    private utils: UtilsService,
    private casesService: CasesService) { }

  createCase(newCase: INewCase, invoiceAttachments: File[], confirmationFile: Blob): Observable<INewCaseRegistered> {
    return this.kreditorWs.post<ServiceResult<INewCaseRegistered>, INewCase>('cases', newCase)
      .pipe(mergeMap(createResponse => {
        const registered = createResponse.result;

        this.uploadConfirmation(registered.caseNumber, confirmationFile).subscribe();

        const uploadInvoicesRequestArray$ = this.buildUploadInvoicesRequestArray(registered.caseNumber, newCase, invoiceAttachments);

        return forkJoin(uploadInvoicesRequestArray$).pipe(
          map((invoicesUploadResult) => {
            const failedUploads = invoicesUploadResult.filter(x => !x.isSuccess);
            registered.failedUploadDocuments = failedUploads.map(x => x.fileName);
            registered.attachmentsUploadSuccess = registered.failedUploadDocuments.length == 0;
            return registered;
          }),
          catchError(() => {
            registered.attachmentsUploadSuccess = false;
            return of(registered)
          }))
      }));
  }

  uploadConfirmation(caseNumber: number, confirmationFile: Blob): Observable<null> {

    const formData = new FormData();
    formData.append('confirmationFile', confirmationFile);

    return this.kreditorWs.postFormData<null>(`cases/${caseNumber}/setConfirmation`, formData);
  }

  uploadCaseDocuments(caseNumber: number, documents: File[]): Observable<IUploadFileResult[]> {
    return this.kreditorWs.postFormData<ServiceResult<IUploadFileResult[]>>(`cases/${caseNumber}/documents/multiple`, this.utils.toFileFormData(documents, 'attachments'))
      .pipe(map(_ => _.result));
  }

  getDefaults(): Observable<INewCase> {
    return this.getInvoiceDefaults().pipe(map(inv => ({
      invoices: [inv],
      countryCode: this.config.getConfig().countryCode,
      debtorHasObjections: false,
      debtorHasReminderIncludedFee: false,
      debtorHasReminderWithPaymentDeadline: false,
      hasDebtCollectionNoticeEUFee: false,
      dateOfReminder: null,
      isRoadFreight: false,
      isFirm: false,
      caseRole: 'Reminder'
    } as INewCase)));
  }

  getInvoiceDefaults(): Observable<INewInvoice> {
    return of({
      amount: 0,
      currencyCode: this.config.getConfig().currencyCode,
      customerReminderFee: null,
      customerReminderDueDate: null
    } as INewInvoice);
  }

  getCountries(): Observable<ICountry[]> {
    return this.kreditorWs.getServiceResult<ICountry[]>('countries')
      .pipe(map(_ => _.result.sort((x, y) => x.name.localeCompare(y.name))));
  }

  getCurrencies(): Observable<string[]> {
    return this.kreditorWs.getServiceResult<string[]>('firm/currencies')
      .pipe(map(_ => _.result));
  }

  getRecentlyCreatedCases(): Observable<IRecentlyCreatedCase[]> {
    return this.kreditorWs.getServiceResult<IRecentlyCreatedCase[]>('cases/recentlyCreated')
      .pipe(map(_ => _.result));
  }

  private buildUploadInvoicesRequestArray(caseNumber: number, newCase: INewCase, invoiceAttachments: File[]): Observable<IUploadFileResult>[] {
    return newCase.invoices.map(x => {
      const invoiceCopy = invoiceAttachments.find(y => y.name == x.invoiceCopyName);
      return this.casesService.uploadInvoiceCopy(caseNumber, x.number, invoiceCopy, false)
        .pipe(
          map(_ => ({ fileName: invoiceCopy.name, isSuccess: true } as IUploadFileResult)),
          catchError(_ => of({ fileName: invoiceCopy.name, isSuccess: false } as IUploadFileResult)));;
    });
  }
}
